import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Divider, FormControlLabel, Grid, Switch, TextField, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { addPolicy, editPolicy, getPolicyById } from '../../../api/endpoints/corporate';
import type { CorporatePolicy } from '../../../api/types/corporate';
import { FormDialog } from '../../../components/common';
import { useFreshEditRow } from '../../../components/useFreshEditRow';

const schema = z.object({
  policyName: z.string().min(1, 'Politika adı gerekli'),
  flightMaximumBudget: z.coerce.number().min(0),
  intFlightMaximumBudget: z.coerce.number().min(0),
  flightBookingMinRezDay: z.coerce.number().min(0),
  canUseBusinessClass: z.boolean(),
  canUseFirstClass: z.boolean(),
  hotelDailyMaxBudget: z.coerce.number().min(0),
  hotelMaxStar: z.coerce.number().min(0).max(5),
  hotelMinRezarvatianDay: z.coerce.number().min(0),
  rentaCarMinRezDay: z.coerce.number().min(0),
  rentACarMaxBudget: z.coerce.number().min(0),
  transferMinRezDay: z.coerce.number().min(0),
  transferMaxBudget: z.coerce.number().min(0),
  busMinRezDay: z.coerce.number().min(0),
  busMaxBudget: z.coerce.number().min(0),
  sendApproval: z.boolean(),
  approvalLevelCount: z.coerce.number().min(0),
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = {
  policyName: '',
  flightMaximumBudget: 0,
  intFlightMaximumBudget: 0,
  flightBookingMinRezDay: 0,
  canUseBusinessClass: false,
  canUseFirstClass: false,
  hotelDailyMaxBudget: 0,
  hotelMaxStar: 0,
  hotelMinRezarvatianDay: 0,
  rentaCarMinRezDay: 0,
  rentACarMaxBudget: 0,
  transferMinRezDay: 0,
  transferMaxBudget: 0,
  busMinRezDay: 0,
  busMaxBudget: 0,
  sendApproval: false,
  approvalLevelCount: 1,
};

function toFormValues(p: CorporatePolicy): FormValues {
  return {
    policyName: p.policyName ?? '',
    flightMaximumBudget: p.flightMaximumBudget ?? 0,
    intFlightMaximumBudget: p.intFlightMaximumBudget ?? 0,
    flightBookingMinRezDay: p.flightBookingMinRezDay ?? 0,
    canUseBusinessClass: p.canUseBusinessClass ?? false,
    canUseFirstClass: p.canUseFirstClass ?? false,
    hotelDailyMaxBudget: p.hotelDailyMaxBudget ?? 0,
    hotelMaxStar: p.hotelMaxStar ?? 0,
    hotelMinRezarvatianDay: p.hotelMinRezarvatianDay ?? 0,
    rentaCarMinRezDay: p.rentaCarMinRezDay ?? 0,
    rentACarMaxBudget: p.rentACarMaxBudget ?? 0,
    transferMinRezDay: p.transferMinRezDay ?? 0,
    transferMaxBudget: p.transferMaxBudget ?? 0,
    busMinRezDay: p.busMinRezDay ?? 0,
    busMaxBudget: p.busMaxBudget ?? 0,
    sendApproval: p.sendApproval ?? false,
    approvalLevelCount: p.approvalLevelCount ?? 1,
  };
}

/** Create/edit dialog for the core Seyahat Politikası (Corporate Travel Policy) fields.
 *  The 4 exception sub-types (FT/CB/HB/HotelReservation) are managed separately on the
 *  policy's "İstisnalar" detail page since they only make sense once a PolicyID exists. */
export function PolicyFormDialog({
  open,
  policy,
  onClose,
}: {
  open: boolean;
  /** null = create mode; otherwise edit mode for this row. */
  policy: CorporatePolicy | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!policy;

  const {
    control,
    handleSubmit,
    register,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: emptyValues,
  });

  // Refetch the fresh row on edit-open; silently falls back to the list row on error
  // (this route can 200 with a business-logic null-ref for some accounts' data).
  const seed = useFreshEditRow({
    open,
    editing: policy,
    queryKey: ['corporate', 'policy', policy?.policyID],
    fetch: async (row) => (await getPolicyById(row.policyID)).policies?.[0],
  });

  useEffect(() => {
    if (open) reset(seed ? toFormValues(seed) : emptyValues);
  }, [open, seed, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      isEdit && policy ? editPolicy({ ...values, policyID: policy.policyID }) : addPolicy(values),
    onSuccess: () => {
      enqueueSnackbar(isEdit ? 'Politika güncellendi.' : 'Politika oluşturuldu.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['corporate', 'policies'] });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));

  return (
    <FormDialog
      open={open}
      title={isEdit ? 'Politikayı Güncelle' : 'Yeni Seyahat Politikası'}
      onClose={onClose}
      onSubmit={submit}
      submitting={mutation.isPending}
      error={mutation.error}
      maxWidth="md"
    >
      <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                label="Politika Adı"
                error={!!errors.policyName}
                helperText={errors.policyName?.message}
                {...register('policyName')}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider textAlign="left">
                <Typography variant="caption" color="text.secondary">
                  Uçak Bileti
                </Typography>
              </Divider>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Yurtiçi Maks. Bütçe"
                {...register('flightMaximumBudget')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Yurtdışı Maks. Bütçe"
                {...register('intFlightMaximumBudget')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Min. Rezervasyon Günü"
                {...register('flightBookingMinRezDay')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                control={control}
                name="canUseBusinessClass"
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                    label="Business Class Kullanabilir"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                control={control}
                name="canUseFirstClass"
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                    label="First Class Kullanabilir"
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider textAlign="left">
                <Typography variant="caption" color="text.secondary">
                  Otel
                </Typography>
              </Divider>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Günlük Maks. Bütçe"
                {...register('hotelDailyMaxBudget')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" type="number" label="Maks. Yıldız" {...register('hotelMaxStar')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Min. Rezervasyon Günü"
                {...register('hotelMinRezarvatianDay')}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider textAlign="left">
                <Typography variant="caption" color="text.secondary">
                  Araç Kiralama
                </Typography>
              </Divider>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Min. Rezervasyon Günü"
                {...register('rentaCarMinRezDay')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Maks. Bütçe"
                {...register('rentACarMaxBudget')}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider textAlign="left">
                <Typography variant="caption" color="text.secondary">
                  Transfer
                </Typography>
              </Divider>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Min. Rezervasyon Günü"
                {...register('transferMinRezDay')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" type="number" label="Maks. Bütçe" {...register('transferMaxBudget')} />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider textAlign="left">
                <Typography variant="caption" color="text.secondary">
                  Otobüs
                </Typography>
              </Divider>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Min. Rezervasyon Günü"
                {...register('busMinRezDay')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" type="number" label="Maks. Bütçe" {...register('busMaxBudget')} />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider textAlign="left">
                <Typography variant="caption" color="text.secondary">
                  Onay
                </Typography>
              </Divider>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Controller
                control={control}
                name="sendApproval"
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                    label="Rezervasyonlar onaya gönderilsin"
                  />
                )}
              />
            </Grid>
            {watch('sendApproval') && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Onay Seviye Sayısı"
                  error={!!errors.approvalLevelCount}
                  helperText={errors.approvalLevelCount?.message}
                  {...register('approvalLevelCount')}
                />
              </Grid>
            )}
      </Grid>
    </FormDialog>
  );
}
